import { useContext, useEffect, useMemo, useRef, useState } from "react";
import PropTypes from "prop-types";
import { gql, useMutation } from "@apollo/client";
import dayjs from "dayjs";
import {
	Card,
	Fade,
	Grid,
	Icon,
	InputLabel,
	Modal,
	Tooltip,
} from "@mui/material";

import SoftBox from "components/SoftBox";
import SoftButton from "components/SoftButton";
import SoftInput from "components/SoftInput";
import SoftTypography from "components/SoftTypography";
import SoftInputNumber from "components/SoftInputNumberUpgrade";
import { MessageManager } from "context";

const SAVE_PAGO_EVENTO = gql`
	mutation savePagoEvento($input: PagoEventoInput!) {
		savePagoEvento(input: $input) {
			pagoEvento {
				id
				idClienteEvento
				fechaPago
				concepto
				banco
				nroTransferencia
				totalPago
				comprobanteFile
				estado
				createdAt
			}
			saldoActual
		}
	}
`;

const modalStyle = {
	position: "absolute",
	top: "50%",
	left: "50%",
	transform: "translate(-50%, -50%)",
	maxHeight: "90%",
	width: { xs: "95%", sm: "70%", md: "45%" },
	overflowY: "auto",
	p: 3,
};

const MAX_FILE_SIZE = 5 * 1024 * 1024;
const ALLOWED_MIME_TYPES = ["application/pdf", "image/jpeg", "image/png"];

function ModalCargarPagoCC({ open, onClose, selected, onSuccess }) {
	const { handleSnackbar } = useContext(MessageManager);
	const [form, setForm] = useState({
		fechaPago: dayjs().format("YYYY-MM-DD"),
		concepto: "",
		banco: "",
		nroTransferencia: "",
		totalPago: null,
	});
	const [file, setFile] = useState(null);
	const [fileError, setFileError] = useState("");
	const inputRef = useRef(null);

	const [savePagoEvento, { loading }] = useMutation(SAVE_PAGO_EVENTO, {
		onCompleted: () => {
			handleSnackbar("Pago registrado correctamente", "success");
			onSuccess?.();
			handleClose();
		},
		onError: (error) => {
			handleSnackbar(error.message, "error");
		},
	});

	const clienteLabel = useMemo(() => {
		if (!selected) return "";
		return selected.nombre || selected.razonSocial || "";
	}, [selected]);

	useEffect(() => {
		if (open) {
			setForm({
				fechaPago: dayjs().format("YYYY-MM-DD"),
				concepto: "",
				banco: "",
				nroTransferencia: "",
				totalPago: null,
			});
			setFile(null);
			setFileError("");
			if (inputRef.current) inputRef.current.value = "";
		}
	}, [open]);

	const handleClose = () => {
		onClose?.();
	};

	const handleChange = (event) => {
		const { name, value } = event.target;
		setForm((prev) => ({ ...prev, [name]: value }));
	};

	const handleTotalChange = (event) => {
		const { value } = event.target;
		setForm((prev) => ({ ...prev, totalPago: value }));
	};

	const handleFileChange = (event) => {
		const newFile = event.target.files?.[0];
		if (!newFile) {
			setFile(null);
			setFileError("");
			return;
		}
		if (newFile.size > MAX_FILE_SIZE) {
			setFile(null);
			setFileError("El archivo debe pesar menos de 5MB");
			if (inputRef.current) inputRef.current.value = "";
			return;
		}
		if (!ALLOWED_MIME_TYPES.includes(newFile.type)) {
			setFile(null);
			setFileError("Formato no permitido (PDF o imagen)");
			if (inputRef.current) inputRef.current.value = "";
			return;
		}
		setFile(newFile);
		setFileError("");
	};

	const handleRemoveFile = () => {
		setFile(null);
		setFileError("");
		if (inputRef.current) inputRef.current.value = "";
	};

	const handleSubmit = async () => {
		if (!selected?.id) {
			handleSnackbar("No se encontró el cliente asociado", "error");
			return;
		}
		const rawMonto = typeof form.totalPago === "number" ? form.totalPago : Number(form.totalPago);
		if (!rawMonto || rawMonto <= 0 || Number.isNaN(rawMonto)) {
			handleSnackbar("Ingrese un importe válido", "warning");
			return;
		}
		const parsedMonto = Number(rawMonto.toFixed(2));
		const variables = {
			input: {
				idClienteEvento: selected.id,
				fechaPago: form.fechaPago ? dayjs(form.fechaPago).format("YYYY-MM-DD") : null,
				concepto: form.concepto || null,
				banco: form.banco || null,
				nroTransferencia: form.nroTransferencia || null,
				totalPago: parsedMonto,
				comprobanteFile: file || null,
			},
		};
		if (!variables.input.comprobanteFile) delete variables.input.comprobanteFile;
		await savePagoEvento({ variables });
	};

	return (
		<Modal
			aria-labelledby="modal-cargar-pago-evento"
			open={open}
			onClose={handleClose}
			closeAfterTransition
		>
			<Fade in={open} mountOnEnter unmountOnExit>
				<Card sx={modalStyle}>
					<SoftBox display="flex" justifyContent="space-between" alignItems="center">
						<SoftTypography variant="h6">
							Registrar pago {clienteLabel ? `- ${clienteLabel}` : ""}
						</SoftTypography>
						<Tooltip title="Cerrar" placement="top">
							<Icon fontSize="medium" onClick={handleClose} sx={{ cursor: "pointer" }}>
								close
							</Icon>
						</Tooltip>
					</SoftBox>
					<SoftBox mt={3}>
						<Grid container spacing={2}>
							<Grid item xs={12} md={6}>
								<InputLabel htmlFor="fechaPago">
									<SoftTypography variant="caption" fontWeight="bold">
										Fecha del pago
									</SoftTypography>
								</InputLabel>
								<SoftInput
									id="fechaPago"
									type="date"
									name="fechaPago"
									value={form.fechaPago}
									onChange={handleChange}
									fullWidth
								/>
							</Grid>
							<Grid item xs={12} md={6}>
								<InputLabel htmlFor="totalPago">
									<SoftTypography variant="caption" fontWeight="bold">
										Importe del pago
									</SoftTypography>
								</InputLabel>
								<SoftInputNumber
									placeholder="0,00"
									name="totalPago"
									value={form.totalPago}
									onChange={handleTotalChange}
								/>
							</Grid>
							<Grid item xs={12}>
								<InputLabel htmlFor="concepto">
									<SoftTypography variant="caption" fontWeight="bold">
										Concepto o descripción
									</SoftTypography>
								</InputLabel>
								<SoftInput
									id="concepto"
									name="concepto"
									value={form.concepto}
									onChange={handleChange}
									placeholder="Describe el pago"
									fullWidth
								/>
							</Grid>
							<Grid item xs={12} md={6}>
								<InputLabel htmlFor="banco">
									<SoftTypography variant="caption" fontWeight="bold">
										Banco
									</SoftTypography>
								</InputLabel>
								<SoftInput
									id="banco"
									name="banco"
									value={form.banco}
									onChange={handleChange}
									placeholder="Banco emisor"
									fullWidth
								/>
							</Grid>
							<Grid item xs={12} md={6}>
								<InputLabel htmlFor="nroTransferencia">
									<SoftTypography variant="caption" fontWeight="bold">
										Nro. de transferencia
									</SoftTypography>
								</InputLabel>
								<SoftInput
									id="nroTransferencia"
									name="nroTransferencia"
									value={form.nroTransferencia}
									onChange={handleChange}
									placeholder="Referencia"
									fullWidth
								/>
							</Grid>
							<Grid item xs={12}>
								<InputLabel htmlFor="comprobanteFile">
									<SoftTypography variant="caption" fontWeight="bold">
										Comprobante (opcional)
									</SoftTypography>
								</InputLabel>
								<SoftBox mt={1}>
									<input
										type="file"
										accept=".pdf,image/jpeg,image/png"
										ref={inputRef}
										onChange={handleFileChange}
									/>
								</SoftBox>
								{file && (
									<SoftBox mt={1} display="flex" alignItems="center" gap={1}>
										<Icon fontSize="small">attach_file</Icon>
										<SoftTypography variant="button" fontWeight="regular">
											{file.name}
										</SoftTypography>
										<Tooltip title="Quitar">
											<Icon fontSize="small" onClick={handleRemoveFile} sx={{ cursor: "pointer" }}>
												close
											</Icon>
										</Tooltip>
									</SoftBox>
								)}
								{fileError && (
									<SoftTypography variant="caption" color="error">
										{fileError}
									</SoftTypography>
								)}
							</Grid>
						</Grid>
					</SoftBox>
					<SoftBox mt={4} display="flex" justifyContent="flex-end" gap={1}>
						<SoftButton color="secondary" variant="outlined" onClick={handleClose} disabled={loading}>
							Cancelar
						</SoftButton>
						<SoftButton color="primary" onClick={handleSubmit} disabled={loading}>
							{loading ? "Guardando..." : "Guardar"}
						</SoftButton>
					</SoftBox>
				</Card>
			</Fade>
		</Modal>
	);
}

ModalCargarPagoCC.propTypes = {
	open: PropTypes.bool,
	onClose: PropTypes.func,
	selected: PropTypes.object,
	onSuccess: PropTypes.func,
};

export default ModalCargarPagoCC;
